'use client';

import React, { useState } from 'react';
import { Search, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AnimatedTooltip } from '../ui/animated-tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Image from 'next/image';
import Link from 'next/link';

const people = [
  {
    id: 1,
    name: 'John Doe',
    designation: 'Software Engineer',
    image: '/avatars/avatar-1.png',
  },
  {
    id: 2,
    name: 'Sarah Smith',
    designation: 'Product Designer',
    image: '/avatars/avatar-2.png',
  },
  {
    id: 3,
    name: 'Mike Johnson',
    designation: 'Marketing Lead',
    image: '/avatars/avatar-3.png',
  },
];

const HeroSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');

  return (
    <div className="relative min-h-[90vh] flex items-center pt-10 pb-16">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-accent/50 to-background dark:from-accent/20 dark:to-background/95 z-[-1]" />
      <div className="absolute top-0 left-0 right-0 h-[600px] bg-accent/30 dark:bg-accent/10 z-[-2]" />

      <div className="container-custom">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="flex flex-col animate-in">
            <div className="mb-4">
              <div className="flex items-center justify-between w-full">
                <span className="px-3 py-1 text-sm bg-accent dark:bg-primary text-primary dark:text-white font-medium rounded-full">
                  Over 10,000+ Jobs Available
                </span>

              </div>
            </div>

            <h1 className="mb-6 leading-tight text-4xl md:text-5xl font-bold dark:text-white">
              Find Your Dream Job <br />
              With <span className="text-primary">JobAssure</span>
            </h1>
            <div className="flex items-center gap-3 mb-8">
              <div className="flex">
                <AnimatedTooltip items={people} />
              </div>
              <div className="flex items-center gap-4 ml-4">
                <p className="font-semibold text-sm dark:text-gray-200">
                  Join 50k+ users
                </p>
                <div className="flex gap-0.5 text-yellow-400 dark:text-yellow-300">
                  {[1, 2, 3, 4, 5].map((_, i) => (
                    <svg
                      key={i}
                      className="w-3.5 h-3.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-lg text-muted-foreground dark:text-muted-foreground/90 mb-8 max-w-lg">
              Discover thousands of job opportunities with all the information
              you need. Find your dream job in top companies around the world.
            </p>

            {/* Search form */}
            <div className="bg-white dark:bg-gray-800/90 rounded-xl p-4 shadow-elevation dark:shadow-accent/5 max-w-3xl animate-in-delay-1">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 flex items-center space-x-2 bg-background/50 dark:bg-background/20 rounded-lg px-3 py-2">
                  <Search className="h-5 w-5 text-muted-foreground dark:text-muted-foreground/90 flex-shrink-0" />
                  <Input
                    type="text"
                    placeholder="Job title or keyword"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-0 bg-transparent shadow-none focus-visible:ring-0 px-0"
                  />
                </div>

                <div className="sm:w-1/3 bg-background/50 dark:bg-background/20 rounded-lg px-3 py-2">
                  <Select value={location} onValueChange={setLocation}>
                    <SelectTrigger className="border-0 bg-transparent shadow-none focus:ring-0 h-9">
                      <div className="flex items-center">
                        <MapPin className="h-5 w-5 text-muted-foreground dark:text-muted-foreground/90 mr-2 flex-shrink-0" />
                        <SelectValue placeholder="Location" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="remote">Remote</SelectItem>
                      <SelectItem value="new-york">New York</SelectItem>
                      <SelectItem value="san-francisco">
                        San Francisco
                      </SelectItem>
                      <SelectItem value="london">London</SelectItem>
                      <SelectItem value="tokyo">Tokyo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button className="sm:w-auto px-8 hover:bg-primary/90 dark:hover:bg-primary/80">
                  Search
                </Button>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm px-2">
                <span className="text-muted-foreground dark:text-muted-foreground/90">
                  Popular:
                </span>
                <Link href="#">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs h-7 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white dark:hover:border-gray-600"
                  >
                    Designer
                  </Button>
                </Link>
                <Link href="#">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs h-7 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white dark:hover:border-gray-600"
                  >
                    Developer
                  </Button>
                </Link>
                <Link href="#">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs h-7 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white dark:hover:border-gray-600"
                  >
                    Marketing
                  </Button>
                </Link>
                <Link href="#">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs h-7 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white dark:hover:border-gray-600"
                  >
                    Remote
                  </Button>
                </Link>
              </div>
            </div>

          </div>

          {/* <div className="hidden md:block relative animate-in-delay-1"> */}
          <div className="relative z-10 overflow-hidden">
            <Image
              src="/hero4.png"
              alt="Person using laptop to search for jobs"
              width={800}
              height={600}
              className="w-full h-full object-cover"
              priority
            />
          </div>

          {/* Decorative elements */}
          {/* <div className="absolute right-20 top-10 w-28 h-28 bg-primary/10 dark:bg-primary/20 rounded-full z-0"></div>
            <div className="absolute left-10 bottom-20 w-40 h-40 bg-accent dark:bg-accent/20 rounded-full z-0"></div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
